import * as React from 'react';
import { View, ScrollView } from 'react-native';
import { Text, Icon } from 'react-native-elements';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import CommerceList from 'components/CommerceList';
import InputSearch from 'components/InputSearch';
import useMediumScreen from 'hooks/useMediumScreen';
import useChangeAuthData from 'hooks/useChangeAuthData';
import AppContext from 'contexts/AppContext';
import OrderContext from 'contexts/OrderContext';
import Commerce from 'types/models/Commerce';
import CommercesScreenProps from 'types/screens/CommercesScreenProps';
import UserRoleType from 'types/enums/UserRoleType';
import { styles } from 'styles/screens/commercesScreen';
import { getTotalBillingAndCommercesAmount } from 'services/ordersService';
import { Loader } from 'components/Loading';
import parseAmounts from 'utils/parseAmounts';
import { SupportedLanguages } from 'types/enums/LanguageType';
import { useCloverPoints } from 'hooks/useCloverPointStatus';
import CloverConnectionStatuses from 'components/CloverConnectionStatuses';
import { useFocusEffect } from '@react-navigation/native';
import CloverConnectionStatusesTypes from 'types/models/CloverConnectionStatuses';
import i18n from 'i18next';

export default ({ navigation }: CommercesScreenProps): JSX.Element => {
  const { t } = useTranslation();
  const { authData, setDisplayMenuModal, setModalData, hideCloverMonitoring } = React.useContext(AppContext);
  const { currentVenue, setCurrentCommerce, setCurrentVenue } = React.useContext(OrderContext);
  const { deleteAuthData } = useChangeAuthData();
  const [inputText, setInputText] = React.useState('');
  const [commercesByVenue, setCommercesByVenue] = React.useState<Commerce[] | undefined>(
    currentVenue?.commerces ?? undefined,
  );
  const [totalBillingCommerces, setTotalBillingCommerces] = React.useState<Record<string, number>>({});
  const mediumScreen = useMediumScreen();
  const [totalBillings, setTotalBillings] = React.useState<number>(0);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isSearchActive, setIsSearchActive] = React.useState(false);
  const [venueCloverPointStatuses, setVenueCloverPointStatuses] = React.useState<CloverConnectionStatusesTypes>({
    online: 0,
    unstable: 0,
    offline: 0,
    whitoutPaper: 0,
    isInactive: 0,
  });

  const currentVenueId = React.useRef<string | null>(null);
  const currentInterval = React.useRef<NodeJS.Timeout | null>(null);

  const isCommerceEditor = authData?.user.role === UserRoleType.CommerceEditor;
  const isVenueEditor = authData?.user.role === UserRoleType.VenueEditor;
  const isFranchiseEditor = authData?.user.role === UserRoleType.FranchiseEditor;
  const currentDateTime = mediumScreen ? moment().format('DD/MM/YYYY hh:mm:ss') : null;

  const { fetchCloverPointsByCodes, cloverStatusesCount } = useCloverPoints({
    currentVenue,
    isCommerceEditor,
    setCurrentVenue,
    setIsLoading,
  });

  const fetchTotalBillingAndCommercesAmount = async () => {
    if (!currentVenue) return;
    if (isCommerceEditor) return;

    try {
      setIsLoading(true);
      const data = await getTotalBillingAndCommercesAmount({
        commerceIds: currentVenue.commerces.map((commerce) => commerce.id),
        billingRange: currentVenue.billingRange,
      });

      setTotalBillings(data.balance);
      setTotalBillingCommerces(data.commerces);
    } catch (error) {
      console.error('Error fetching total billing and commerces amount:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const findUserCommerce = () => {
    if (!currentVenue) return;
    if (isCommerceEditor) {
      const userCommerce = currentVenue.commerces.reduce((value) => value);
      if (userCommerce) {
        setCurrentCommerce(userCommerce);
      }
    }
  };

  const handleSearch = async () => {
    if (!inputText || inputText === '-1') {
      setIsSearchActive(false);
      setCommercesByVenue(currentVenue?.commerces);
    } else {
      setIsSearchActive(true);
      const patternRegex = new RegExp(`(${inputText})`, 'gi');
      const filteredCommerces = currentVenue?.commerces?.filter((commerce) => patternRegex.test(commerce.name));
      if (filteredCommerces && filteredCommerces.length > 0) {
        setCommercesByVenue(filteredCommerces);
      } else {
        setCommercesByVenue([]);
      }
    }
  };

  // React.useEffect(() => {
  //   const handleBillingIntervalTime = async () => {
  //     const billingIntervalTimeConfig = await getBillingIntervalTimeConfig();
  //     setBillingIntervalTime(billingIntervalTimeConfig.value * 1000);
  //   };

  //   handleBillingIntervalTime();
  // }, []);

  React.useEffect(() => {
    if (!isSearchActive) {
      setCommercesByVenue(currentVenue?.commerces ?? undefined);
    }
  }, [currentVenue?.commerces, isSearchActive]);

  useFocusEffect(
    React.useCallback(() => {
      if (!currentVenue) {
        setTimeout(() => {
          navigation.replace('VenueSelectionScreen');
        }, 500);
        return;
      }

      const venueChanged = currentVenueId.current !== currentVenue.id;
      if (venueChanged) {
        currentVenueId.current = currentVenue.id;
        findUserCommerce();
      }

      if (isSearchActive) {
        return;
      }

      fetchTotalBillingAndCommercesAmount();
      fetchCloverPointsByCodes();

      /*currentInterval.current = setInterval(() => {
        fetchTotalBillingAndCommercesAmount();
        fetchCloverPointsByCodes();
        setVenueCloverPointStatuses(cloverStatusesCount(currentVenue));
      }, billingIntervalTime);*/

      return () => {
        if (currentInterval.current) {
          clearInterval(currentInterval.current);
          currentInterval.current = null;
        }
      };
    }, [currentVenue?.id, isSearchActive]),
  );

  React.useEffect(() => {
    if (!currentVenue) return;
    setVenueCloverPointStatuses(cloverStatusesCount(currentVenue));
  }, [currentVenue?.cloverPoints]);

  return (
    <View style={styles.container}>
      {isLoading && <Loader position="top-center" />}
      <View style={mediumScreen ? styles.header : styles.headerSmall}>
        <Icon
          name="menu-outline"
          type="ionicon"
          color="#000000"
          size={40}
          containerStyle={mediumScreen ? styles.iconLarge : styles.iconSmall}
          onPress={() => setDisplayMenuModal(true)}
        />
        {mediumScreen ? (
          <View style={styles.upperTextContainerLarge}>
            <Text style={styles.textTitleLarge}>{mediumScreen ? t('commerces') : currentVenue?.name}</Text>
            <View style={{ marginTop: 25, marginLeft: 60 }}>
              <Text
                style={{
                  fontSize: 18,
                  fontWeight: '400',
                  lineHeight: 20,
                  letterSpacing: 0.25,
                  textAlign: 'center',
                  color: '#001639',
                }}
              >{`${currentDateTime}hs`}</Text>
            </View>
          </View>
        ) : (
          <Text style={styles.textSmall}>{mediumScreen ? t('commerces') : currentVenue?.name}</Text>
        )}
        {mediumScreen ? (
          <InputSearch
            value={inputText}
            setInput={setInputText}
            handleSearch={handleSearch}
            placeholderText={t('search-by-commerce')}
            keyboardType="default"
            isMediumScreen={mediumScreen}
          />
        ) : (
          <Icon
            name="person-circle-sharp"
            type="ionicon"
            color="#001C47"
            size={40}
            containerStyle={mediumScreen ? styles.iconLarge : styles.iconSmall}
            onPress={() =>
              setModalData({
                description: t('logout'),
                generalColor: currentVenue?.appColor ?? '#001C47',
                action: async () => {
                  await deleteAuthData();
                },
              })
            }
          />
        )}
      </View>
      {!mediumScreen && (
        <Text
          style={{
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignSelf: 'flex-start',
            marginLeft: 40,
            marginBottom: 10,
            fontSize: 16,
            fontWeight: '500',
            lineHeight: 24,
            letterSpacing: 0,
            textAlign: 'left',
          }}
        >
          {t('commerces')}
        </Text>
      )}
      {(isVenueEditor || isFranchiseEditor) && (
        <View
          style={[
            mediumScreen ? styles.totalContainerLarge : styles.totalContainerSmall,
            { backgroundColor: currentVenue?.appColor ?? '#001C47' },
          ]}
        >
          <View
            style={[
              mediumScreen ? styles.totalTextContainerAdjustmentsLarge : styles.totalTextContainerAdjustmentsSmall,
            ]}
          >
            <Text style={mediumScreen ? styles.totalTextTitleLarge : styles.totalTextTitleSmall}>
              {t('total-balance')}{' '}
            </Text>
            <Text style={mediumScreen ? styles.totalTextBalanceLarge : styles.totalTextBalanceSmail}>
              {parseAmounts(totalBillings, i18n.language as SupportedLanguages)}
            </Text>
          </View>
        </View>
      )}
      {!hideCloverMonitoring && (
        <View style={[styles.totalContainerSmall, { flexDirection: 'column', alignItems: 'center', marginTop: 10 }]}>
          <Text
            style={[{ marginBottom: 10, fontSize: 16, fontWeight: 'bold', textAlign: 'center', color: '#6b6b6bff' }]}
          >
            {t('clover-connection-status.for-venue')} {currentVenue?.name}
          </Text>
          <View style={{ width: mediumScreen ? 400 : '90%', alignItems: 'center' }}>
            <CloverConnectionStatuses
              isForVenue
              cloverPointStatuses={venueCloverPointStatuses}
              navigation={navigation}
              venueCode={currentVenue?.code}
            />
          </View>
        </View>
      )}

      <ScrollView keyboardShouldPersistTaps="handled" scrollEnabled={true} style={styles.scrollItemsContainer}>
        <View
          style={[
            mediumScreen ? styles.topRowOnListCommercesLarge : styles.topRowOnListCommercesSmall,
            { backgroundColor: currentVenue?.appColor ?? '#000000' },
          ]}
        >
          <View style={{ flex: 1 }}>
            <Text style={styles.topRowText}>{t('name')}</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.topRowText}>{t('total')}</Text>
          </View>
        </View>

        {currentVenue && commercesByVenue && (
          <CommerceList commerces={commercesByVenue} totalBillings={totalBillingCommerces} navigation={navigation} />
        )}
      </ScrollView>
    </View>
  );
};
